import React, { useEffect, useState } from 'react';
import { Form, Image, Input, Modal, Skeleton } from 'antd';
import getApplicantComments, {
  createComment,
  deleteCommentReq,
  editCommentReq,
} from '@services/comments/comments-http.service';
import DownArrow from '@assets/svgs/black-down-arrow.svg';
import RightArrow from '@assets/svgs/black-right-arrow.svg';
import AvatarIcon from '@assets/svgs/avatar-icon.svg';
import TrashIcon from '@assets/svgs/trash-red-icon.svg';
import PencilIcon from '@assets/svgs/red-pencil-icon.svg';
import useNotification from '@hooks/notification/useNotification';
import StateEnum from '@constants/state.enum';
import { ApplicantCommentsModel } from '@models/comments';
import UiButton from '@components/ui/button/UiButton';
import { useTranslation } from 'next-i18next';
import { dateFromNow } from '@services/date/date-from-now';
import styles from './ApplicantComments.module.scss';

type ApplicantCommentsProps = {
  applicantId: string;
  fetchApplicants?: () => void;
};

const ApplicantComments: React.FC<ApplicantCommentsProps> = ({ applicantId, fetchApplicants }) => {
  const { t: translate } = useTranslation('applicant');
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [showSubmit, setShowSubmit] = useState<boolean>(false);
  const [showSubmitEdit, setShowSubmitEdit] = useState<boolean>(false);
  const [disableSubmitEditButton, setDisableSubmitEditButton] = useState<boolean>(true);
  const [disableSubmitButton, setDisableSubmitButton] = useState<boolean>(true);
  const [textWarningMessage, setTextWarningMessage] = useState<boolean>(false);
  const [textWarningCountdown, setTextWarningCountdown] = useState<number>(50);
  const [editTextWarningMessage, setEditTextWarningMessage] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editTextWarningCountdown, setEditTextWarningCountdown] = useState<number>(50);
  const [editComment, setEditComment] = useState<{ id: number; comment: string }>({ id: -1, comment: '' });
  const [deleteComment, setDeleteComment] = useState<{
    id: number;
    comment: string;
    author: string;
    createdDate: Date;
  }>({
    id: -1,
    comment: '',
    author: '',
    createdDate: new Date(),
  });
  const [comments, setComments] = useState<ApplicantCommentsModel>({ count: 0, items: [] });

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const fetchApplicantComments = async () => {
    try {
      setLoading(true);
      const commentsElem: any = await getApplicantComments(applicantId);
      setComments(commentsElem);
      setLoading(false);
    } catch (e: any) {
      generalError();
      setLoading(false);
    }
  };

  useEffect(() => {
    setOpen(false);
    fetchApplicantComments();
  }, [applicantId]);

  const addComment = async () => {
    setLoading(true);
    try {
      const commentsElem: ApplicantCommentsModel = await createComment(applicantId, form.getFieldValue('comment'));
      await setComments(commentsElem);
      await setLoading(false);
      await setShowSubmit(false);
      await setTextWarningMessage(false);
      if (fetchApplicants) await fetchApplicants();
      await setEditComment({ id: -1, comment: '' });
      form.resetFields();
    } catch (e: any) {
      generalError();
      setShowSubmit(false);
      setLoading(false);
    }
  };

  const fetchEditComment = async (commentId: string) => {
    setLoading(true);
    try {
      const commentsElem: ApplicantCommentsModel = await editCommentReq(
        applicantId,
        commentId,
        editForm.getFieldValue('editComment'),
      );
      await setComments(commentsElem);
      await setLoading(false);
      await setShowSubmitEdit(false);
      await setEditTextWarningMessage(false);
      await setEditComment({ id: -1, comment: '' });
      if (fetchApplicants) await fetchApplicants();
      await editForm.resetFields();
    } catch (e: any) {
      generalError();
      setShowSubmitEdit(false);
      setLoading(false);
    }
  };

  const fetchDeleteComment = async (commentId: string) => {
    setLoadingDelete(true);
    try {
      const commentsElem: ApplicantCommentsModel = await deleteCommentReq(applicantId, commentId);
      await setComments(commentsElem);
      if (fetchApplicants) await fetchApplicants();
      await setLoadingDelete(false);
      setShowModal(false);
    } catch (e: any) {
      generalError();
      setLoadingDelete(false);
    }
  };

  const emptyState = () => {
    return (
      <div className={styles.wrapEmptyState}>
        <div className={styles.emptyIcon}>
          <Image src="/assets/images/empty-comments.png" preview={false} alt="Empty comments" />
        </div>
        <div className={`large-body-text ${styles.emptyText}`}>Τίποτα για να δείτε εδώ!</div>
        <div className={`main-body-text ${styles.emptySubtext}`}>
          Φαίνεται πως δεν υπάρχουν σχόλια για να σου εμφανίσουμε.
        </div>
        <div className={`main-body-text ${styles.emptySubtext}`}>
          Μοιράσου τις σκέψεις σου αφήνoντας ένα <b>νέο</b> σχόλιο.
        </div>
      </div>
    );
  };

  const commentEditMenu = (id: number, comment: string, author: string, createdDate: Date) => {
    return (
      <div className={styles.wrapEditMenu}>
        <div
          className={styles.menuIcon}
          role="button"
          tabIndex={0}
          onClick={async () => {
            await setEditComment({ id, comment });
            await editForm.resetFields();
          }}
          onKeyDown={async () => {
            await setEditComment({ id, comment });
            await editForm.resetFields();
          }}
        >
          <PencilIcon data-testid="edit-menu-item" />
        </div>
        <div
          className={styles.menuIcon}
          role="button"
          tabIndex={0}
          onClick={() => {
            setShowModal(true);
            setDeleteComment({ id, comment, author, createdDate });
          }}
          onKeyDown={() => {
            setShowModal(true);
            setDeleteComment({ id, comment, author, createdDate });
          }}
        >
          <TrashIcon data-testid="delete-menu-item" />
        </div>
      </div>
    );
  };

  const renderEditComment = (id: number) => {
    return (
      <Form
        key={id}
        name="editComment"
        layout="inline"
        className={`antd-overwrite ${styles.wrapEditCommentForm}`}
        form={editForm}
      >
        <div className={styles.commentForm}>
          <div className={styles.wrapFormAvatar}>
            <AvatarIcon style={{ height: '32px' }} />
          </div>
          <div className={styles.textareaSubmitButton}>
            <Form.Item name="editComment" className={styles.wrapCommentInput} initialValue={editComment.comment}>
              <Input.TextArea
                onChange={(value) => {
                  if (value.target.value.length > 550) {
                    setEditTextWarningMessage(true);
                    setEditTextWarningCountdown(600 - value.target.value.length);
                  } else {
                    setEditTextWarningMessage(false);
                  }
                  if (!value.target.value) {
                    return setDisableSubmitEditButton(true);
                  }
                  return setDisableSubmitEditButton(false);
                }}
                onFocus={() => {
                  setShowSubmitEdit(!showSubmitEdit);
                  setDisableSubmitEditButton(false);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowSubmitEdit(!showSubmitEdit);
                  }, 180);
                }}
                onKeyDown={() => {}}
                placeholder={translate('comments_or_notes')}
                autoSize={{ minRows: 1, maxRows: 8 }}
                data-testid="edit-comment-input"
                maxLength={600}
                value={editComment.comment}
              />
            </Form.Item>
            <div className={showSubmitEdit ? styles.wrapCommentActions : styles.hideSubmitButton}>
              <div className={styles.wrapActionButtons}>
                <UiButton
                  type="primary"
                  size="small"
                  onClick={() => fetchEditComment(id.toString())}
                  className={showSubmitEdit ? styles.submitComment : styles.hideSubmitButton}
                  disabled={disableSubmitEditButton}
                >
                  {translate('update_button')}
                </UiButton>
                <UiButton
                  type="default"
                  size="small"
                  onClick={async () => {
                    await setEditComment({ id: -1, comment: '' });
                    await setEditTextWarningMessage(false);
                    await editForm.resetFields();
                  }}
                  className={showSubmitEdit ? styles.submitComment : styles.hideSubmitButton}
                  disabled={disableSubmitEditButton}
                >
                  {translate('cancel_button')}
                </UiButton>
              </div>
              {editTextWarningMessage && showSubmitEdit ? (
                <p className={`h6 ${styles.warningText}`}>
                  {translate('characters_remaining', { characters: editTextWarningCountdown })}
                </p>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>
      </Form>
    );
  };

  const renderComments = () => {
    return loading ? (
      <>
        <Skeleton avatar paragraph={{ rows: 1 }} active />
        <Skeleton avatar paragraph={{ rows: 1 }} active />
        <Skeleton avatar paragraph={{ rows: 1 }} active />
      </>
    ) : (
      <div>
        {comments.items?.map((comment) => {
          return editComment.id === comment.id ? (
            renderEditComment(comment.id)
          ) : (
            <div key={comment.id} className={styles.wrapComment}>
              {commentEditMenu(
                comment.id,
                comment.comment,
                `${comment.user.firstname} ${comment.user.lastname}`,
                comment.createdDate,
              )}
              <div className={styles.wrapUserIcon}>
                <AvatarIcon style={{ height: '32px' }} />
              </div>
              <div className={styles.commentTexts}>
                <div className={styles.wrapUserAndDate}>
                  <div className={`h5 ${styles.commentUser}`}>
                    {`${comment.user.firstname} ${comment.user.lastname}`}
                  </div>
                  <div className={`small-caps-regular ${styles.commentDate}`}>{dateFromNow(comment.createdDate)}</div>
                </div>
                <div className={`main-body-text ${styles.commentText}`}>{comment.comment}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLastComment = () => {
    if (comments.items?.length >= 1 && !open) {
      return editComment.id === comments.items[comments.items.length - 1].id ? (
        renderEditComment(comments.items[comments.items.length - 1].id)
      ) : (
        <div className={open ? styles.hideCommentContent : styles.wrapComment} data-testid="comment-element">
          {commentEditMenu(
            comments.items[comments.items.length - 1].id,
            comments.items[comments.items.length - 1].comment,
            `${comments.items[comments.items.length - 1].user.firstname} ${
              comments.items[comments.items.length - 1].user.lastname
            }`,
            comments.items[comments.items.length - 1].createdDate,
          )}
          <div className={styles.wrapUserIcon}>
            <AvatarIcon style={{ height: '32px' }} />
          </div>
          <div className={styles.commentTexts}>
            <div className={styles.wrapUserAndDate}>
              <div className={`h5 ${styles.commentUser}`}>
                {`${comments.items[comments.items.length - 1].user.firstname} ${
                  comments.items[comments.items.length - 1].user.lastname
                }`}
              </div>
              <div className={`small-caps-regular ${styles.commentDate}`}>
                {dateFromNow(comments.items[comments.items.length - 1].createdDate)}
              </div>
            </div>
            <div className={`main-body-text ${styles.commentText}`}>
              {comments.items[comments.items.length - 1].comment}
            </div>
          </div>
        </div>
      );
    }

    const renderCommentsComp = () => {
      if (comments && comments?.items?.length) {
        return renderComments();
      }
      return emptyState();
    };

    return <div className={open ? styles.wrapCommentContent : styles.hideCommentContent}>{renderCommentsComp()}</div>;
  };

  return (
    <>
      <div className={styles.wrapComments}>
        <div className={styles.separator}>
          <div
            className={styles.wrapCommentsTitle}
            role="button"
            onClick={() => {
              setOpen(!open);
            }}
            onKeyDown={() => {
              setOpen(!open);
            }}
            tabIndex={0}
            data-testid="comments-arrow"
          >
            <div className={`h6 ${styles.titleText}`}>
              {translate('comments')} ({comments.items?.length})
            </div>
            {open ? (
              <div className={styles.downArrow}>
                <DownArrow />
              </div>
            ) : (
              <div className={styles.rightArrow}>
                <RightArrow />
              </div>
            )}
          </div>
        </div>
        {renderLastComment()}
        <Form name="addComment" layout="inline" className={`antd-overwrite ${styles.wrapCommentForm}`} form={form}>
          <div className={styles.commentForm}>
            <div className={styles.wrapFormAvatar}>
              <AvatarIcon style={{ height: '32px' }} />
            </div>
            <Form.Item name="comment" className={styles.wrapCommentInput} required>
              <Input.TextArea
                onChange={(value) => {
                  if (value.target.value.length > 550) {
                    setTextWarningMessage(true);
                    setTextWarningCountdown(600 - value.target.value.length);
                  } else {
                    setTextWarningMessage(false);
                  }
                  if (!value.target.value) {
                    return setDisableSubmitButton(true);
                  }
                  return setDisableSubmitButton(false);
                }}
                onFocus={() => setShowSubmit(!showSubmit)}
                onBlur={() => {
                  setTimeout(() => {
                    setShowSubmit(!showSubmit);
                  }, 180);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
                placeholder={translate('comments_or_notes')}
                autoSize={{ minRows: 1, maxRows: 8 }}
                data-testid="comment-input"
                maxLength={600}
              />
            </Form.Item>
          </div>
          <div className={showSubmit ? styles.wrapCommentActions : styles.hideSubmitButton}>
            <UiButton
              type="primary"
              size="small"
              onClick={addComment}
              className={showSubmit ? styles.submitComment : styles.hideSubmitButton}
              disabled={disableSubmitButton}
            >
              {translate('save')}
            </UiButton>
            {textWarningMessage && showSubmit ? (
              <p className={`h6 ${styles.warningText}`}>
                {translate('characters_remaining', { characters: textWarningCountdown })}
              </p>
            ) : (
              <div />
            )}
          </div>
        </Form>
      </div>
      <Modal
        title={translate('delete_comment')}
        footer={null}
        open={showModal}
        onCancel={() => setShowModal(false)}
        className={styles.deleteCommentModal}
      >
        <div className={`main-body-text ${styles.subTitle}`}>{translate('comment_permanent_delete')}</div>
        <div className={styles.wrapDeleteComment}>
          <div className={styles.deleteUserIcon}>
            <AvatarIcon style={{ height: '32px' }} />
          </div>
          <div className={styles.wrapDeleteDetails}>
            <div className={styles.wrapAuthorDate}>
              <div className={`h5 ${styles.deleteAuthor}`}>{deleteComment.author}</div>
              <div className={`small-caps-regular ${styles.deleteDate}`}>{dateFromNow(deleteComment.createdDate)}</div>
            </div>
            <div className={`main-body-text ${styles.deleteCommentDescription}`}>{deleteComment.comment}</div>
          </div>
        </div>
        <div className={styles.deleteActions}>
          <UiButton
            size="small"
            type="primary"
            onClick={() => fetchDeleteComment(deleteComment.id.toString())}
            disabled={loadingDelete}
          >
            {translate('delete')}
          </UiButton>
          <UiButton size="small" onClick={() => setShowModal(false)}>
            {translate('cancel')}
          </UiButton>
        </div>
      </Modal>
    </>
  );
};

export default ApplicantComments;
